import { useState, type FC } from 'react';
import BaseLoader from 'components/atoms/base-loader';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import { useGetPatientSnapshot } from 'infrastructure/hooks/patient/use-get-snapshot';
import { useTranslation } from 'react-i18next';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { DateFormatter } from 'infrastructure/functions';
import dayjs from 'dayjs';
import i18n from 'infrastructure/i18n';
import { DateFormats, PresetKeyEnum } from 'infrastructure/enums';
import { endOfDay, startOfDay } from 'date-fns';
import InformationTooltip from 'components/atoms/information-tooltip';
import BaseButton from 'components/atoms/baseButton';
import TrendUpIcon from 'components/atoms/icons/trend-up-icon';
import TrendDownIcon from 'components/atoms/icons/trend-down-icon';
import InformationIcon from 'components/atoms/icons/informationIcon';
import { useGetPatientSnapshotSummary } from 'infrastructure/hooks/patient/use-get-snapshot-summary';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import SearchIcon from 'components/atoms/icons/search.svg?react';
import {
  CalendarOutlined,
  TruckOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  DownOutlined,
  RightOutlined,
  SafetyCertificateFilled,
} from '@ant-design/icons';
import classNames from 'classnames';

import SummaryModal from './summary-modal';
import ReadingCard from './reading-card';
import s from './styles.module.scss';
import SummariesDropdown, {
  SUMMARY_BUTTONS,
} from './summary-modal/dropdown-btn';
import StatusTag from './status-tag';

import type { TSnapshotSummaryType } from 'infrastructure/interfaces/i-patient-snapshot';
import type { IValue } from 'components/atoms/base-range-datepicker';

interface PatientSnapshotProps {
  patientGuid: string;
}

const presets = [
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountDays', {
          count: 7,
        })}
      </span>
    ),
    value: [
      dayjs().add(-6, 'd').startOf('D').toDate(),
      dayjs().endOf('D').toDate(),
    ] as [Date, Date],
    key: PresetKeyEnum.LastSevenDays,
    index: 7,
  },
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountDays', {
          count: 30,
        })}
      </span>
    ),
    value: [
      dayjs().add(-29, 'd').startOf('D').toDate(),
      dayjs().endOf('D').toDate(),
    ] as [Date, Date],
    key: PresetKeyEnum.LastThirtyDays,
    index: 30,
  },
  {
    label: (
      <span data-cy="ranges">
        {i18n.t('dates.lastCountMonths', {
          count: 3,
        })}
      </span>
    ),
    value: [
      dayjs().add(-2, 'M').startOf('D').toDate(),
      dayjs().endOf('D').toDate(),
    ] as [Date, Date],
    key: PresetKeyEnum.ThreeMonthsPast,
  },
];

const PatientSnapshot: FC<PatientSnapshotProps> = ({ patientGuid }) => {
  const [openModalType, setOpenModalType] =
    useState<TSnapshotSummaryType | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [dates, setDates] = useState<IValue | null>(presets[1].value);
  const { patientInfo, loading } = usePatientInfo({ patientGuid });
  const { patientSnapshot, isLoading } = useGetPatientSnapshot({
    patientGuid,
    dates,
  });
  const { patientSnapshotSummary, isLoading: isLoadingSummary } =
    useGetPatientSnapshotSummary({
      patientGuid,
      type: openModalType,
      dates,
    });
  const { t } = useTranslation();
  const twoWaySync = patientInfo?.interactionStatus;
  const {
    lastContact,
    clinicEscalationCount,
    lastDeviceDeliveryDate,
    isEligible,
  } = patientSnapshot ?? {};

  const lastContactType =
    lastContact?.type === 'sms'
      ? lastContact.type.toUpperCase()
      : lastContact?.type;

  const escalationCount = clinicEscalationCount ?? 0;
  const escalationTimes = escalationCount > 1 ? 'times' : 'time';

  const loader = (
    <div className={s['loading-box']}>
      <BaseLoader loading />
    </div>
  );

  const tooltipContent = (
    <div className={s.tooltip}>
      <p data-title>
        <InformationIcon />
        {t('patientSnapshot.indicators')}
      </p>
      <p>
        <TrendUpIcon />
        {t('patientSnapshot.trendUpText')}
      </p>
      <p>
        <TrendDownIcon />
        {t('patientSnapshot.trendDownText')}
      </p>
      <p>
        {t('patientSnapshot.noIndicator')}
        {': '}
        {t('patientSnapshot.noIndicatorText')}
      </p>
    </div>
  );

  const content = (
    <>
      <div className={s.header}>
        <div className={s['header-left']}>
          <div className={s['title-wrapper']}>
            <BaseButton
              type="secondary"
              className={s['toggle-button']}
              icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
              onClick={() => setIsExpanded(!isExpanded)}
            />
            <h3 className={s.title}>{t('patientSnapshot.patientSnapshot')}</h3>
            <div className={s.summary}>
              <SummariesDropdown setOpenModalIdx={setOpenModalType} />
            </div>
          </div>
          {isExpanded && (
            <div className={s['last-data-wrapper']}>
              <p className={s['last-contact']}>
                <CalendarOutlined className={s['meta-icon']} />
                {`${t('patientSnapshot.lastContact')}: `}
                <span className={s['data-value']}>
                  {patientSnapshot?.lastContact ? (
                    <>
                      {lastContactType}
                      {' - '}
                      {DateFormatter({
                        date: lastContact?.date,
                        format: DateFormats['MM/dd/yyyy'],
                      })}
                    </>
                  ) : (
                    t('labels.noData')
                  )}
                </span>
              </p>

              <p className={s['last-contact']}>
                <TruckOutlined className={s['meta-icon']} />
                {`${t('patientSnapshot.lastShipmentDate')}: `}
                <span className={s['data-value']}>
                  {lastDeviceDeliveryDate ? (
                    <>
                      {DateFormatter({
                        date: lastDeviceDeliveryDate,
                        format: DateFormats['MM/dd/yyyy'],
                      })}
                    </>
                  ) : (
                    t('labels.noData')
                  )}
                </span>
              </p>

              <p className={s['escalation-count']}>
                <ExclamationCircleOutlined className={s['meta-icon']} />
                {`${t('patientSnapshot.clinicEscalation')}: `}
                <span
                  className={classNames(s['clinical-value'], {
                    [s['clinical-value-error']]: escalationCount > 0,
                  })}
                >
                  {clinicEscalationCount ?? 0} {escalationTimes}
                </span>
              </p>
            </div>
          )}
        </div>
        {isExpanded && (
          <div className={s['header-right']}>
            <div className={s['header-right-block']}>
              <div className={s['patient-info-wrapper']}>
                <StatusTag
                  value={isEligible}
                  label={t('patientSnapshot.eligibility')}
                  icon={<SafetyCertificateFilled />}
                />
                <StatusTag value={twoWaySync} label={t('labels.twoWaySync')} />
              </div>
              <div className={s['date-wrapper']}>
                <div className={s.date}>
                  <BaseRangeDatepicker
                    withPresets
                    value={dates || undefined}
                    onChange={(range) => {
                      if (!range) return;
                      const [start, end] = range;
                      setDates([startOfDay(start), endOfDay(end)]);
                    }}
                    presets={() => presets}
                    allowClear={false}
                  />
                </div>
              </div>
              <InformationTooltip
                icon={
                  <BaseButton
                    type="secondary"
                    className={s['tooltip-btn']}
                    icon={<InfoCircleOutlined />}
                  />
                }
                contentWrapperClassName={s['tooltip-content-wrapper']}
                content={tooltipContent}
              />
            </div>
          </div>
        )}
      </div>
      {isExpanded && (
        <>
          {patientSnapshot?.data?.length === 0 ? (
            <div className={s['empty-data-wrapper']}>
              <EmptyTablePlaceholder
                text={t('patientSnapshot.noDataFound')}
                icon={<SearchIcon width={70} height={70} />}
                column
              />
            </div>
          ) : (
            <div className={s.body}>
              <div className={s['readings-wrapper']}>
                {patientSnapshot?.data?.map((reading, index) => {
                  return (
                    <ReadingCard
                      key={index}
                      type={reading.type}
                      values={reading.data}
                      statusPercentages={reading.data.statusPercentages}
                      trend={reading.data.trend}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div id="patient-snapshot" className={s.wrapper}>
        {loading || isLoading ? loader : content}
      </div>
      {openModalType && (
        <SummaryModal
          open={!!openModalType}
          onCancel={() => setOpenModalType(null)}
          subTitle={SUMMARY_BUTTONS[openModalType]}
          summary={patientSnapshotSummary?.summary}
          loading={isLoadingSummary}
        />
      )}
    </>
  );
};
export default PatientSnapshot;
